import { useState, useEffect } from 'react'
import { format } from 'date-fns'
import { useEventMutations } from '../hooks/useCalendarEvents'
import { Button } from '../../../components/ui/Button'
import { Spinner } from '../../../components/ui/Spinner'
import { cn, getEventColor } from '../../../lib/utils'
import { ConflictModal } from '../../../components/ConflictModal'
import { supabase } from '../../../lib/supabase'
import { EVENT_PALETTE } from '../../../styles/tokens'

const RECUR_OPTIONS = [
  { value: '',         label: 'Does not repeat' },
  { value: 'DAILY',   label: 'Daily' },
  { value: 'WEEKLY',  label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'YEARLY',  label: 'Yearly' },
  { value: 'custom',  label: 'Custom…' },
]

const toLocalInput = (iso) => {
  if (!iso) return ''
  return iso.slice(0, 16)
}

const toIso = (local) => {
  if (!local) return null
  return new Date(local).toISOString()
}

const buildRRule = ({ freq, interval, endType, endCount, endDate }) => {
  if (!freq) return null
  let rule = `FREQ=${freq};INTERVAL=${interval || 1}`
  if (endType === 'count' && endCount) rule += `;COUNT=${endCount}`
  if (endType === 'until' && endDate) {
    const d = new Date(endDate)
    rule += `;UNTIL=${d.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`
  }
  return rule
}

const parseRRule = (ruleStr) => {
  if (!ruleStr) return { freq: '', interval: 1, endType: 'never', endCount: '', endDate: '' }
  const parts = {}
  for (const part of ruleStr.split(';')) {
    const [k, v] = part.split('=')
    parts[k] = v
  }
  return {
    freq:     parts.FREQ  ?? '',
    interval: parseInt(parts.INTERVAL ?? '1'),
    endType:  parts.COUNT ? 'count' : parts.UNTIL ? 'until' : 'never',
    endCount: parts.COUNT ?? '',
    endDate:  parts.UNTIL
      ? parts.UNTIL.replace(/(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z/, '$1-$2-$3T$4:$5:$6')
      : '',
  }
}

const defaultStart = (day) => {
  const d = day ? new Date(day) : new Date()
  d.setHours(9, 0, 0, 0)
  return d.toISOString()
}

export const EventForm = ({ event, calendars, members, initialDay, onClose, onSaved }) => {
  const editing = !!event

  const { createEvent, updateEvent } = useEventMutations()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [conflict, setConflict] = useState(null)
  const [pendingPayload, setPendingPayload] = useState(null)

  const [title,       setTitle]       = useState(event?.title       ?? '')
  const [description, setDescription] = useState(event?.description ?? '')
  const [location,    setLocation]    = useState(event?.location     ?? '')
  const [calendarId,  setCalendarId]  = useState(event?.calendar_id  ?? calendars?.[0]?.id ?? '')
  const [eventColor,  setEventColor]  = useState(event?.event_color  ?? '')
  const [isAllDay,    setIsAllDay]    = useState(event?.is_all_day   ?? false)
  const [startTime,   setStartTime]   = useState(toLocalInput(event?.start_time ?? defaultStart(initialDay)))
  const [endTime,     setEndTime]     = useState(toLocalInput(event?.end_time ?? ''))
  const [driverId,    setDriverId]    = useState(event?.driver_id   ?? '')
  const [memberIds,   setMemberIds]   = useState(
    (event?.event_members ?? []).map((em) => em.user_id),
  )

  const initialRR = parseRRule(event?.recurrence_rule ?? '')
  const [recurFreq,     setRecurFreq]     = useState(initialRR.freq)
  const [recurInterval, setRecurInterval] = useState(initialRR.interval)
  const [recurEndType,  setRecurEndType]  = useState(initialRR.endType)
  const [recurEndCount, setRecurEndCount] = useState(initialRR.endCount)
  const [recurEndDate,  setRecurEndDate]  = useState(initialRR.endDate)
  const [showCustom,    setShowCustom]    = useState(false)

  const toggleMember = (uid) =>
    setMemberIds((prev) => prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid])

  const handleRecurSelect = (val) => {
    if (val === 'custom') {
      setShowCustom(true)
    } else {
      setShowCustom(false)
      setRecurFreq(val)
    }
  }

  const recurrenceRule = showCustom || recurFreq
    ? buildRRule({ freq: recurFreq, interval: recurInterval, endType: recurEndType, endCount: recurEndCount, endDate: recurEndDate })
    : null

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!title.trim()) { setError('Title is required'); return }
    if (!calendarId)    { setError('Select a calendar'); return }
    setSaving(true)
    setError('')

    const payload = {
      title:          title.trim(),
      description:    description || null,
      location:       location || null,
      calendar_id:    calendarId,
      event_color:    eventColor || null,
      is_all_day:     isAllDay,
      start_time:     toIso(startTime),
      end_time:       endTime ? toIso(endTime) : null,
      driver_id:      driverId || null,
      recurrence_rule: recurrenceRule,
      memberIds,
    }

    try {
      if (editing) {
        const result = await updateEvent({
          id: event._originalId ?? event.id,
          localUpdatedAt: event.updated_at,
          ...payload,
        })
        if (result?.conflict) {
          setPendingPayload(payload)
          setConflict(result.conflict)
          setSaving(false)
          return
        }
      } else {
        await createEvent(payload)
      }
      onSaved?.()
      onClose()
    } catch (err) {
      setError(err.message)
      setSaving(false)
    }
  }

  const handleKeepMine = async () => {
    setSaving(true)
    setConflict(null)
    try {
      const { error: err } = await supabase
        .from('events')
        .update(pendingPayload)
        .eq('id', event._originalId ?? event.id)
      if (err) throw err
      onSaved?.()
      onClose()
    } catch (err) {
      setError(err.message)
      setSaving(false)
    }
  }

  const selectFreqLabel = () => {
    if (showCustom || (recurFreq && !RECUR_OPTIONS.find((o) => o.value === recurFreq && o.value !== 'custom'))) {
      return 'Custom…'
    }
    return RECUR_OPTIONS.find((o) => o.value === recurFreq)?.label ?? 'Does not repeat'
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-0 overflow-hidden" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <form
        className="relative bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90dvh] flex flex-col shadow-card"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-border">
          <h2 className="text-base font-bold text-text-primary">
            {editing ? 'Edit Event' : 'New Event'}
          </h2>
          <button type="button" onClick={onClose} className="text-text-muted hover:text-text-primary p-1">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 min-h-0 px-5 py-4 space-y-4">
          {/* Title */}
          <input
            type="text"
            placeholder="Event title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full rounded-base border border-border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />

          {/* Calendar */}
          <select
            value={calendarId}
            onChange={(e) => setCalendarId(e.target.value)}
            className="w-full rounded-base border border-border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {(calendars ?? []).map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          {/* Event color */}
          <div>
            <div className="text-xs font-semibold text-text-muted mb-2 uppercase tracking-wide flex items-center gap-2">
              Color
              {eventColor && (
                <button type="button" onClick={() => setEventColor('')}
                  className="text-[10px] text-text-muted hover:text-rose-500 font-normal normal-case tracking-normal">
                  Reset to calendar color
                </button>
              )}
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {EVENT_PALETTE.map((c) => (
                <button key={c} type="button" onClick={() => setEventColor(c === eventColor ? '' : c)}
                  className={cn('w-7 h-7 rounded-full transition-transform flex-shrink-0 flex items-center justify-center',
                    eventColor === c && 'ring-2 ring-offset-1 ring-primary scale-110')}
                  style={{ backgroundColor: c }}>
                  {eventColor === c && (
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="2.5"><path d="m2 6 3 3 5-5"/></svg>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* All-day toggle */}
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={isAllDay}
              onChange={(e) => setIsAllDay(e.target.checked)}
              className="sr-only"
            />
            <div
              aria-hidden="true"
              className={cn(
                'w-10 rounded-full transition-colors relative flex-shrink-0',
                isAllDay ? 'bg-primary' : 'bg-border',
              )}
              style={{ height: 22 }}
            >
              <span
                className={cn(
                  'absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform',
                  isAllDay ? 'translate-x-5' : 'translate-x-0.5',
                )}
              />
            </div>
            <span className="text-sm text-text-primary">All day</span>
          </label>

          {/* Start / End */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div>
              <label className="text-xs font-medium text-text-muted mb-1 block">Start</label>
              <input
                type={isAllDay ? 'date' : 'datetime-local'}
                value={isAllDay ? startTime.slice(0, 10) : startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full rounded-base border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-text-muted mb-1 block">End</label>
              <input
                type={isAllDay ? 'date' : 'datetime-local'}
                value={isAllDay ? endTime.slice(0, 10) : endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full rounded-base border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          {/* Location */}
          <input
            type="text"
            placeholder="Location (optional)"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            className="w-full rounded-base border border-border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />

          {/* Description */}
          <textarea
            placeholder="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full rounded-base border border-border px-4 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
          />

          {/* Members */}
          {(members ?? []).length > 0 && (
            <div>
              <div className="text-xs font-semibold text-text-muted mb-2 uppercase tracking-wide">Tag Members</div>
              <div className="flex flex-wrap gap-2">
                {members.map((m) => {
                  if (!m.profiles) return null
                  const p       = m.profiles
                  const active  = memberIds.includes(p.id)
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => toggleMember(p.id)}
                      className={cn(
                        'text-xs font-medium px-3 py-1 rounded-full transition-opacity border',
                        active ? 'opacity-100 text-white' : 'opacity-50',
                      )}
                      style={{
                        backgroundColor: active ? (p.member_color ?? '#7c6faf') : 'transparent',
                        borderColor: p.member_color ?? '#7c6faf',
                        color: active ? '#fff' : (p.member_color ?? '#7c6faf'),
                      }}
                    >
                      {p.display_name}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Driver */}
          <div>
            <label className="text-xs font-semibold text-text-muted mb-1 block uppercase tracking-wide">Driver (optional)</label>
            <select
              value={driverId}
              onChange={(e) => setDriverId(e.target.value)}
              className="w-full rounded-base border border-border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">No driver</option>
              {(members ?? []).filter((m) => m.profiles).map((m) => (
                <option key={m.profiles.id} value={m.profiles.id}>{m.profiles.display_name}</option>
              ))}
            </select>
          </div>

          {/* Recurrence */}
          <div>
            <label className="text-xs font-semibold text-text-muted mb-1 block uppercase tracking-wide">Repeat</label>
            <select
              value={showCustom ? 'custom' : recurFreq}
              onChange={(e) => handleRecurSelect(e.target.value)}
              className="w-full rounded-base border border-border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {RECUR_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>

            {showCustom && (
              <div className="mt-3 space-y-3 p-3 bg-background rounded-xl border border-border">
                <div className="flex flex-col sm:flex-row gap-3">
                  <div>
                    <label className="text-xs text-text-muted mb-1 block">Frequency</label>
                    <select
                      value={recurFreq}
                      onChange={(e) => setRecurFreq(e.target.value)}
                      className="w-full rounded-base border border-border px-3 py-2 text-sm"
                    >
                      <option value="DAILY">Daily</option>
                      <option value="WEEKLY">Weekly</option>
                      <option value="MONTHLY">Monthly</option>
                      <option value="YEARLY">Yearly</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-text-muted mb-1 block">Every</label>
                    <input
                      type="number"
                      min="1"
                      value={recurInterval}
                      onChange={(e) => setRecurInterval(e.target.value)}
                      className="w-full rounded-base border border-border px-3 py-2 text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs text-text-muted mb-1 block">End</label>
                  <div className="flex gap-3">
                    {[
                      { v: 'never', label: 'Never' },
                      { v: 'count', label: 'After' },
                      { v: 'until', label: 'On date' },
                    ].map((o) => (
                      <label key={o.v} className="flex items-center gap-1.5 text-sm cursor-pointer">
                        <input
                          type="radio"
                          name="recurEnd"
                          value={o.v}
                          checked={recurEndType === o.v}
                          onChange={() => setRecurEndType(o.v)}
                        />
                        {o.label}
                      </label>
                    ))}
                  </div>
                  {recurEndType === 'count' && (
                    <div className="flex items-center gap-2 mt-2">
                      <input
                        type="number"
                        min="1"
                        value={recurEndCount}
                        onChange={(e) => setRecurEndCount(e.target.value)}
                        className="w-24 rounded-base border border-border px-3 py-2 text-sm"
                      />
                      <span className="text-sm text-text-muted">occurrences</span>
                    </div>
                  )}
                  {recurEndType === 'until' && (
                    <input
                      type="date"
                      value={recurEndDate}
                      onChange={(e) => setRecurEndDate(e.target.value)}
                      className="mt-2 w-full rounded-base border border-border px-3 py-2 text-sm"
                    />
                  )}
                </div>
              </div>
            )}
          </div>

          {error && <p className="text-sm text-rose-600">{error}</p>}
        </div>

        <div className="px-5 py-4 border-t border-border flex gap-2 flex-shrink-0">
          <Button type="button" variant="secondary" size="sm" className="flex-1" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" size="sm" className="flex-1" disabled={saving}>
            {saving ? <Spinner size="sm" /> : editing ? 'Save Changes' : 'Create Event'}
          </Button>
        </div>
      </form>

      {conflict && (
        <ConflictModal
          localRecord={pendingPayload}
          serverRecord={conflict.serverRecord}
          onKeepMine={handleKeepMine}
          onUseServer={() => { setConflict(null); onClose() }}
        />
      )}
    </div>
  )
}
